import { useEffect, useMemo, useState } from "react";

import {
  adminApi,
  type AdminCronJob,
  type AdminCronJobTemplate,
  type AdminNotificationTarget,
} from "../../api/admin";
import { Modal } from "../../components/Modal";
import { BriefingInstructionField } from "../../components/admin/BriefingInstructionField";
import { CronInput } from "../../components/admin/CronInput";
import { NotificationTargetEditor } from "../../components/admin/NotificationTargetEditor";
import { ScopePicker } from "../../components/admin/ScopePicker";
import { useToast } from "../../components/toast/context";

interface Props {
  open: boolean;
  onClose: () => void;
  /** When set, the modal is in edit mode for that row; otherwise create. */
  editing?: AdminCronJob | null;
}

interface FormState {
  name: string;
  scope_type: string;
  scope_id: string;
  schedule: string;
  instruction: string;
  model: string;
  timeout: number;
  enabled: boolean;
  notification_target: AdminNotificationTarget | null;
}

const EMPTY: FormState = {
  name: "",
  scope_type: "user",
  scope_id: "",
  schedule: "",
  instruction: "",
  model: "",
  timeout: 600,
  enabled: true,
  notification_target: null,
};

function fromExisting(row: AdminCronJob): FormState {
  return {
    name: row.name,
    scope_type: row.scope_type,
    scope_id: row.scope_id,
    schedule: row.schedule ?? "",
    instruction: row.instruction ?? "",
    model: row.model ?? "",
    timeout: row.timeout ?? 600,
    enabled: row.enabled,
    notification_target: row.notification_target ?? null,
  };
}

export function CronJobModal({ open, onClose, editing }: Props) {
  const toast = useToast();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [scheduleValid, setScheduleValid] = useState(false);
  const [instructionValid, setInstructionValid] = useState(true);

  const templates = adminApi.cronJobs.templates();
  const scopeTypes = adminApi.scopes.types();
  const create = adminApi.cronJobs.create();
  const patch = adminApi.cronJobs.patch(editing?.id ?? "");

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setForm(editing ? fromExisting(editing) : EMPTY);
      setInstructionValid(true);
    }
  }, [open, editing]);

  const validTokensForScope = useMemo(() => {
    const t = scopeTypes.data?.find((s) => s.type_name === form.scope_type);
    return t?.valid_tokens ?? [];
  }, [scopeTypes.data, form.scope_type]);

  const canSave =
    !!form.name &&
    !!form.scope_type &&
    !!form.scope_id &&
    scheduleValid &&
    instructionValid;

  function applyTemplate(name: string) {
    const tpl: AdminCronJobTemplate | undefined = templates.data?.find(
      (t) => t.name === name,
    );
    if (!tpl) return;
    setForm((prev) => ({
      ...prev,
      name: prev.name || tpl.name,
      schedule: prev.schedule || tpl.schedule || "",
      instruction: prev.instruction || tpl.instruction,
      model: prev.model || tpl.model || "",
      timeout: tpl.timeout || prev.timeout,
    }));
  }

  function save() {
    const payload = {
      name: form.name,
      scope_type: form.scope_type,
      scope_id: form.scope_id,
      schedule: form.schedule,
      instruction: form.instruction,
      model: form.model || null,
      timeout: form.timeout,
      enabled: form.enabled,
      notification_target: form.notification_target,
    };

    const onSuccess = () => {
      toast.ok("Saved. Schedule changes apply immediately — no restart needed.");
      onClose();
    };
    const onError = (err: unknown) =>
      toast.error((err as Error).message || "Save failed");

    if (editing) {
      patch.mutate(payload, { onSuccess, onError });
    } else {
      create.mutate(payload, { onSuccess, onError });
    }
  }

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={editing ? `Edit cron job` : "Add cron job"}
        eyebrow="Admin · Cron jobs"
        widthClass="max-w-3xl"
        footer={
          <div className="flex items-center justify-end gap-2">
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-main"
              disabled={!canSave || create.isPending || patch.isPending}
              onClick={save}
            >
              {editing ? "Save changes" : "Create"}
            </button>
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          {/* Name + template picker */}
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="label">Job name</label>
              <input
                className="input"
                value={form.name}
                onChange={(e) =>
                  setForm({ ...form, name: e.target.value })
                }
                placeholder="morning-brief"
                disabled={!!editing}
              />
            </div>
            <div>
              <label className="label">Use template</label>
              <select
                className="input"
                value=""
                onChange={(e) => {
                  if (e.target.value) applyTemplate(e.target.value);
                  e.target.value = "";
                }}
              >
                <option value="">Pick a template…</option>
                {(templates.data ?? []).map((t) => (
                  <option key={t.name} value={t.name}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Scope */}
          <ScopePicker
            scopeType={form.scope_type}
            scopeId={form.scope_id}
            onChange={(next) => setForm({ ...form, ...next })}
          />

          {/* Schedule */}
          <CronInput
            value={form.schedule}
            onChange={(v) => setForm({ ...form, schedule: v })}
            onValidityChange={setScheduleValid}
          />

          {/* Briefing instruction */}
          <BriefingInstructionField
            value={form.instruction}
            scopeType={form.scope_type}
            validTokens={validTokensForScope}
            onChange={(next) => setForm({ ...form, instruction: next })}
            onValidityChange={setInstructionValid}
          />

          {/* Model / Timeout / Enabled */}
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="label">Model (optional)</label>
              <input
                className="input"
                value={form.model}
                onChange={(e) => setForm({ ...form, model: e.target.value })}
                placeholder="leave empty to use agent default"
              />
            </div>
            <div>
              <label className="label">Timeout (seconds)</label>
              <input
                type="number"
                className="input"
                min={10}
                max={86400}
                value={form.timeout}
                onChange={(e) =>
                  setForm({ ...form, timeout: parseInt(e.target.value) || 600 })
                }
              />
            </div>
          </div>

          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) =>
                setForm({ ...form, enabled: e.target.checked })
              }
            />
            <span>Enabled</span>
          </label>

          {/* Notification target override */}
          <NotificationTargetEditor
            value={form.notification_target}
            onChange={(v) => setForm({ ...form, notification_target: v })}
            helpText="Leave as 'use scope default' unless this row needs to post somewhere different than the scope's default."
          />
        </div>
      </Modal>
    </>
  );
}
